import { Button } from '@heidi/design-system/button';
import { TypographyH5, TypographyP2 } from '@heidi/design-system/typography';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerFooter,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';

interface DeleteChatDialogProps {
  open: boolean;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** When > 1, shows bulk delete messaging. */
  count?: number;
}

const DeleteChatDialog = ({
  open,
  isDeleting,
  onCancel,
  onConfirm,
  count = 1,
}: DeleteChatDialogProps) => {
  const { t } = useTranslations();

  const isBulk = count > 1;
  const title = isBulk
    ? t('deleteChatModal.bulkTitle' as TranslationKey, { count })
    : t('deleteChatModal.title' as TranslationKey);
  const description = isBulk
    ? t('deleteChatModal.bulkDescription' as TranslationKey, { count })
    : t('deleteChatModal.description' as TranslationKey);

  return (
    <DialogDrawer
      modal
      dismissible
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogDrawerContent className="max-w-full p-6 sm:max-w-md">
        <DialogDrawerHeader className="text-left">
          <DialogDrawerTitle>
            <TypographyH5>{title}</TypographyH5>
          </DialogDrawerTitle>
          <DialogDrawerDescription asChild>
            <TypographyP2>{description}</TypographyP2>
          </DialogDrawerDescription>
        </DialogDrawerHeader>
        <DialogDrawerFooter className="flex flex-row justify-end gap-x-2 pt-4">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isDeleting}>
            {t('deleteChatModal.cancel' as TranslationKey)}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={isDeleting}
            onClick={onConfirm}
          >
            {isDeleting
              ? t('deleteChatModal.deleting' as TranslationKey)
              : t('deleteChatModal.delete' as TranslationKey)}
          </Button>
        </DialogDrawerFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default DeleteChatDialog;
